import React from 'react';
import { usePageTitle } from '../hooks/usePageTitle';

const Eula: React.FC = () => {
  usePageTitle('Terms of Service');

  return (
    <div className="legal-page">
      <div className="legal-container">
        <header className="legal-header">
          <h1>End-User License Agreement & Terms of Service</h1>
          <p className="legal-effective-date">Effective Date: March 19, 2026</p>
        </header>

        <main className="legal-content">
          <section className="legal-section">
            <h2>1. Agreement to Terms</h2>
            <p>
              These End-User License Agreement and Terms of Service (collectively, "Terms") constitute a binding legal agreement between you ("User," "you," or "your") and Patriot Consulting Technology Group, LLC ("Company," "we," "us," or "our").
            </p>
            <p>
              By creating an account, accessing our platform, purchasing a course, or using any services provided by Patriot Academy Premium, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, do not create an account or use our services.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Service Description</h2>
            <p>
              Patriot Academy Premium provides online training services including:
            </p>
            <ul className="legal-list">
              <li>Expert-led Microsoft 365 security courses delivered via Teams webinars</li>
              <li>Hands-on lab environments for practical learning</li>
              <li>Course materials, resources, and recorded sessions (where applicable)</li>
              <li>Certification preparation and guidance</li>
              <li>Student support and instructor interaction</li>
            </ul>
            <p>
              The Company reserves the right to modify, update, or discontinue courses and services at any time. Material changes will be communicated to affected users.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. Account and User Responsibilities</h2>

            <h3>3.1 Account Creation</h3>
            <p>
              You agree to provide accurate, complete, and current information when creating an account. You are responsible for:
            </p>
            <ul className="legal-list">
              <li>Maintaining the confidentiality of your password and account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of unauthorized access or use</li>
            </ul>

            <h3>3.2 Appropriate Use</h3>
            <p>You agree not to:</p>
            <ul className="legal-list">
              <li>Share your login credentials with others or allow others to use your account</li>
              <li>Access or use the platform for unlawful purposes or in violation of these Terms</li>
              <li>Attempt to gain unauthorized access to restricted areas or systems</li>
              <li>Harass, threaten, or abuse instructors, staff, or other students</li>
              <li>Interfere with the normal operation of the platform</li>
              <li>Circumvent security measures or authentication systems</li>
            </ul>

            <h3>3.3 Account Suspension</h3>
            <p>
              The Company reserves the right to suspend or terminate your account at any time if you violate these Terms, engage in misconduct, or if you fail to pay for services. Suspension or termination may result in loss of access to course materials and enrollments.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Payment Terms and Refund Policy</h2>

            <h3>4.1 Pricing</h3>
            <p>
              Course pricing is displayed on the platform at the time of enrollment. Prices are subject to change without notice for future purchases. The price you agree to at checkout is the price you will be charged.
            </p>

            <h3>4.2 Payment Processing</h3>
            <p>
              Payments are processed securely through QuickBooks Online (Intuit). By providing payment information, you authorize the Company and Intuit to charge your account for the course fee. You agree to pay all applicable taxes.
            </p>

            <h3>4.3 Refund Policy</h3>
            <p>
              Refunds are available under the following conditions:
            </p>
            <ul className="legal-list">
              <li><strong>7+ Days Before Course:</strong> Full refund of course tuition</li>
              <li><strong>3–6 Days Before Course:</strong> 50% refund of course tuition</li>
              <li><strong>Within 48 Hours of Course Start:</strong> No refund available</li>
              <li><strong>Exceptions:</strong> Refunds due to course cancellation by the Company or documented technical issues that prevent course access</li>
            </ul>
            <p>
              Refund requests must be submitted via email to <a href="mailto:training@patriotconsulting.com">training@patriotconsulting.com</a>. Refunds are processed within 5–7 business days.
            </p>

            <h3>4.4 Failed Payments</h3>
            <p>
              If a payment fails, the Company may suspend your account access until payment is received. Late payments may result in additional fees and loss of enrollment eligibility.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Intellectual Property Rights</h2>

            <h3>5.1 Company Ownership</h3>
            <p>
              All course content, including lectures, materials, videos, slides, lab instructions, and resources, are the exclusive intellectual property of Patriot Consulting Technology Group, LLC and are protected by copyright and other intellectual property laws.
            </p>

            <h3>5.2 License Grant</h3>
            <p>
              The Company grants you a limited, non-exclusive, non-transferable, revocable license to access and use course materials solely for your personal, non-commercial educational purposes. This license terminates upon course completion or account termination.
            </p>

            <h3>5.3 Restrictions on Use</h3>
            <p>
              You agree not to:
            </p>
            <ul className="legal-list">
              <li>Record, screenshot, or capture course content without written permission</li>
              <li>Redistribute, share, sell, or license course materials to third parties</li>
              <li>Create derivative works or modifications of course content</li>
              <li>Reverse engineer or attempt to extract underlying systems or methodologies</li>
              <li>Use course content for commercial purposes or to train others</li>
            </ul>

            <h3>5.4 Your Content</h3>
            <p>
              To the extent you create content or provide feedback within the platform (assignments, comments, etc.), you grant the Company a non-exclusive license to use such content for educational, improvement, and quality assurance purposes.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Lab Environment Usage</h2>

            <h3>6.1 Purpose and Access</h3>
            <p>
              Lab environments are provided exclusively for training and educational purposes during course instruction. You may access labs only during scheduled course sessions and the period specified by your instructor.
            </p>

            <h3>6.2 Limitations</h3>
            <ul className="legal-list">
              <li>Lab environments are <strong>not for production use</strong></li>
              <li>Labs are subject to resource limits and may be reset or destroyed without notice</li>
              <li>Lab environments are reset after each course session</li>
              <li>No data persistence is guaranteed; do not store critical or sensitive information in labs</li>
              <li>The Company is not responsible for data loss in lab environments</li>
            </ul>

            <h3>6.3 Acceptable Use</h3>
            <p>
              You agree to use lab environments only for the assigned course exercises. Prohibited activities include:
            </p>
            <ul className="legal-list">
              <li>Testing production workloads or applications</li>
              <li>Mining cryptocurrency or other resource-intensive tasks</li>
              <li>Unauthorized penetration testing or security attacks</li>
              <li>Accessing resources or systems outside the scope of the course</li>
            </ul>

            <h3>6.4 Lab Suspension</h3>
            <p>
              The Company reserves the right to suspend lab access if you violate these usage terms or engage in disruptive or harmful activities.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Limitation of Liability</h2>

            <h3>7.1 Educational Purpose</h3>
            <p>
              Patriot Academy Premium provides training and educational content. The Company makes no guarantee that course completion will result in employment, salary increases, or certification success. Individual outcomes depend on factors beyond the Company's control, including your effort, prior knowledge, workplace environment, and external testing requirements.
            </p>

            <h3>7.2 Disclaimer of Warranties</h3>
            <p>
              THE SERVICES AND CONTENT ARE PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. THE COMPANY DISCLAIMS ALL IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <h3>7.3 Limitation of Damages</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>

            <h3>7.4 Maximum Liability</h3>
            <p>
              IN NO EVENT SHALL THE COMPANY'S TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID FOR THE COURSE, EXCLUSIVE OF REFUNDS ISSUED UNDER THE REFUND POLICY.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Termination and Suspension</h2>

            <h3>8.1 Termination by Company</h3>
            <p>
              The Company may terminate or suspend your account and services at any time, with or without cause, upon written notice. Cause includes, but is not limited to:
            </p>
            <ul className="legal-list">
              <li>Violation of these Terms</li>
              <li>Non-payment of fees</li>
              <li>Harassment or misconduct</li>
              <li>Unauthorized credential sharing</li>
              <li>Use of lab environments in violation of Section 6</li>
              <li>Enrollment by an individual or organization that the Company reasonably determines to be a direct competitor in the managed security services, security consulting, or security training markets. The Company reserves the right to refuse service, cancel enrollments, and issue refunds at its sole discretion to protect its proprietary curriculum, methodologies, and competitive interests.</li>
            </ul>

            <h3>8.2 Effect of Termination</h3>
            <p>
              Upon termination, your access to the platform, courses, and lab environments is immediately revoked. Refund eligibility is determined by the Refund Policy. Sections on Intellectual Property Rights, Limitation of Liability, and Governing Law survive termination.
            </p>

            <h3>8.3 Termination by User</h3>
            <p>
              You may request account closure by contacting <a href="mailto:training@patriotconsulting.com">training@patriotconsulting.com</a>. Refund eligibility depends on the timing relative to course start (see Section 4.3).
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Governing Law and Dispute Resolution</h2>

            <h3>9.1 Governing Law</h3>
            <p>
              These Terms are governed by and construed in accordance with the laws of the State of California, without regard to its conflict-of-law principles. You agree to submit to the jurisdiction of the state and federal courts located in California.
            </p>

            <h3>9.2 Dispute Resolution</h3>
            <p>
              Before initiating legal proceedings, both parties agree to attempt good-faith negotiation. If a dispute arises, you agree to provide written notice to the Company with reasonable detail to allow for resolution.
            </p>

            <h3>9.3 Limitation Period</h3>
            <p>
              Any legal action or claim must be brought within one year of the event giving rise to the claim, or it will be barred.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. General Provisions</h2>

            <h3>10.1 Entire Agreement</h3>
            <p>
              These Terms, along with our Privacy Policy, constitute the entire agreement between you and the Company regarding the use of our services. Any prior or contemporaneous agreements are superseded.
            </p>

            <h3>10.2 Severability</h3>
            <p>
              If any provision of these Terms is found to be unenforceable, that provision shall be modified to the minimum extent necessary to make it enforceable, and all other provisions remain in full effect.
            </p>

            <h3>10.3 Waiver</h3>
            <p>
              The Company's failure to enforce any right or provision does not constitute a waiver of that right or provision. Waivers must be in writing and signed by an authorized representative.
            </p>

            <h3>10.4 Amendments</h3>
            <p>
              The Company may modify these Terms at any time. Material changes will be communicated to you. Continued use of the platform constitutes acceptance of the updated Terms.
            </p>

            <h3>10.5 Survival</h3>
            <p>
              Sections on Intellectual Property Rights, Limitation of Liability, Governing Law, and Dispute Resolution survive termination or expiration of these Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact Information</h2>
            <p>
              For questions, concerns, or notices regarding these Terms, please contact:
            </p>
            <div className="contact-info">
              <p>
                <strong>Patriot Consulting Technology Group, LLC</strong><br />
                17192 Murphy Avenue, #14067<br />
                Irvine, CA 92623
              </p>
              <p>
                <strong>Email:</strong> <a href="mailto:training@patriotconsulting.com">training@patriotconsulting.com</a><br />
                <strong>Phone:</strong> <a href="tel:1-844-560-4630">1-844-560-4630</a><br />
                <strong>Website:</strong> <a href="https://patriotconsulting.com" target="_blank" rel="noopener noreferrer">patriotconsulting.com</a>
              </p>
            </div>
          </section>

          <section className="legal-section">
            <h2>12. Acknowledgment</h2>
            <p>
              By using Patriot Academy Premium, you acknowledge that you have read and understood these Terms and agree to be bound by them. If you do not agree, please discontinue use of the platform immediately.
            </p>
          </section>
        </main>
      </div>
    </div>
  );
};

export default Eula;
